import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import Image from 'next/image';
import { useToast } from '@/hooks/use-toast';
import { ImageCropper } from '@/components/ui/image-cropper';

interface AddAccountFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (account: {
    name: string;
    industry: string;
    website?: string;
    type: string;
    city: string;
    stateRegion: string;
    postalCode: string;
    numberOfEmployees: number;
    annualRevenue: number;
    timeZone: string;
    description: string;
    linkedInPage: string;
    logo: string;
  }) => void;
}

const accountTypes = [
  "PROSPECT",
  "CUSTOMER",
  "PARTNER",
  "RESELLER",
  "OTHER"
] as const;

type AccountType = typeof accountTypes[number];

export function AddAccountForm({ isOpen, onClose, onSubmit }: AddAccountFormProps) {
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [website, setWebsite] = useState('');
  const [type, setType] = useState<AccountType>("PROSPECT");
  const [city, setCity] = useState('');
  const [stateRegion, setStateRegion] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [numberOfEmployees, setNumberOfEmployees] = useState('');
  const [annualRevenue, setAnnualRevenue] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [description, setDescription] = useState('');
  const [linkedInPage, setLinkedInPage] = useState('');
  const [logo, setLogo] = useState('');
  const [isLoadingLogo, setIsLoadingLogo] = useState(false);
  const [logoOptions, setLogoOptions] = useState<string[]>([]);
  const [selectedLogoIndex, setSelectedLogoIndex] = useState(0);
  const { toast } = useToast();
  const [cropperIsOpen, setCropperIsOpen] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string>('');

  const handleLogoSelect = (index: number) => {
    setSelectedLogoIndex(index);
    setLogo(logoOptions[index]);
  };

  const handleLogoUpload = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (!target.files || target.files.length === 0) return;

    const file = target.files[0];
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File Type",
        description: "Please upload an image file.",
        variant: "destructive",
      });
      return;
    }

    const tempUrl = URL.createObjectURL(file);
    setTempImageUrl(tempUrl);
    setCropperIsOpen(true);
  };

  const handleCroppedImage = async (blob: Blob) => {
    setIsLoadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'cropped-logo.png');

      const uploadResponse = await fetch('/api/upload/logo', {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) throw new Error('Failed to upload logo');

      const data = await uploadResponse.json();

      if (data.url) {
        setLogoOptions([data.url]);
        setLogo(data.url);
        setSelectedLogoIndex(0);
      }
    } catch (error) {
      console.error('Error uploading cropped logo:', error);
      toast({
        title: "Error",
        description: "Failed to upload logo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingLogo(false);
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name) {
      toast({
        title: "Required Fields Missing",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      await onSubmit({
        name,
        industry,
        website: website || undefined,
        type,
        city,
        stateRegion,
        postalCode,
        numberOfEmployees: parseInt(numberOfEmployees, 10) || 0,
        annualRevenue: parseFloat(annualRevenue) * 1000000 || 0,
        timeZone,
        description,
        linkedInPage,
        logo,
      });

      toast({
        title: "Success",
        description: "Account created successfully!",
      });

      setName('');
      setIndustry('');
      setWebsite('');
      setType("PROSPECT");
      setCity('');
      setStateRegion('');
      setPostalCode('');
      setNumberOfEmployees('');
      setAnnualRevenue('');
      setTimeZone('');
      setDescription('');
      setLinkedInPage('');
      setLogo('');
      setLogoOptions([]);
      setSelectedLogoIndex(0);

      onClose();
    } catch (error) {
      console.error('Error creating account:', error);
      toast({
        title: "Error",
        description: "Failed to create account. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Add New Account</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2">
            <div className="grid gap-6 py-4">
              <div className="space-y-2">
                <Label htmlFor="accountName">Account Name</Label>
                <Input
                  id="accountName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter account name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website Domain</Label>
                <div className="flex gap-2">
                  <Input
                    id="website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="Enter website URL"
                    className="flex-grow"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = handleLogoUpload;
                      input.click();
                    }}
                    disabled={isLoadingLogo}
                  >
                    Upload Logo
                  </Button>
                </div>
              </div>

              {logoOptions.length > 0 && (
                <div className="space-y-2">
                  <Label>Logo</Label>
                  <div className="flex gap-2">
                    {logoOptions.map((logoUrl, index) => (
                      <div
                        key={logoUrl}
                        className={`cursor-pointer border p-1 rounded ${index === selectedLogoIndex ? 'border-blue-500' : 'border-gray-200'
                          }`}
                        onClick={() => handleLogoSelect(index)}
                      >
                        {logoUrl && logoUrl.length > 0 && (
                          <Image
                            src={logoUrl.startsWith("http") ? `/api/content-library/proxy-image?url=${encodeURIComponent(logoUrl)}` : logoUrl}
                            alt={`Logo option ${index + 1}`}
                            width={50}
                            height={50}
                            unoptimized
                            className="object-contain"
                            onError={() => {
                              setLogoOptions(prev => prev.filter(url => url !== logoUrl));
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    placeholder="Enter industry"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select value={type} onValueChange={(value: AccountType) => setType(value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select account type" />
                    </SelectTrigger>
                    <SelectContent>
                      {accountTypes.map((accountType) => (
                        <SelectItem key={accountType} value={accountType}>
                          {accountType.charAt(0) + accountType.slice(1).toLowerCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Enter city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stateRegion">State/Region</Label>
                  <Input
                    id="stateRegion"
                    value={stateRegion}
                    onChange={(e) => setStateRegion(e.target.value)}
                    placeholder="Enter state or region"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postalCode">Postal Code</Label>
                  <Input
                    id="postalCode"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="Enter postal code"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="numberOfEmployees">Number of Employees</Label>
                  <Input
                    id="numberOfEmployees"
                    value={numberOfEmployees}
                    onChange={(e) => setNumberOfEmployees(e.target.value)}
                    placeholder="Enter number of employees"
                    type="number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="annualRevenue">Annual Revenue (in millions)</Label>
                  <Input
                    id="annualRevenue"
                    value={annualRevenue}
                    onChange={(e) => setAnnualRevenue(e.target.value)}
                    placeholder="Enter annual revenue in millions"
                    type="number"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="timeZone">Time Zone</Label>
                  <Input
                    id="timeZone"
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}
                    placeholder="Enter time zone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="linkedInPage">LinkedIn Page</Label>
                  <Input
                    id="linkedInPage"
                    value={linkedInPage}
                    onChange={(e) => setLinkedInPage(e.target.value)}
                    placeholder="Enter LinkedIn page URL"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter account description"
                  rows={3}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit}>
              Add Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageCropper
        isOpen={cropperIsOpen}
        onClose={() => {
          setCropperIsOpen(false);
          URL.revokeObjectURL(tempImageUrl);
          setTempImageUrl('');
        }}
        imageSrc={tempImageUrl}
        onCropComplete={handleCroppedImage}
      />
    </>
  );
}
